import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { hasRole, clientRole, primaryRole, permsForRole } from "../lib/roles";
import { useRoleTabs } from "../hooks/useRoleTabs";
import { useNotifications } from "../contexts/NotificationContext";
import { useClientNotifications } from "../hooks/useClientNotifications";
import { useMasterNotifications } from "../hooks/useMasterNotifications";
import { useMediaQuery } from "react-responsive";
import logo from "../assets/logo.png";
import LanguageSelector from "./LanguageSelector";
import NotificationBell from "./NotificationBell";
import ProfileSwitcher from "./ProfileSwitcher";
import CargoSwitcher from "./CargoSwitcher";
import { useActiveCargo } from "../lib/activeCargo";
import {
  FaBars,
  FaTimes,
  FaCog,
  FaSignOutAlt,
  FaBox,
  FaChevronDown,
  FaHome,
  FaUsers,
  FaUserTie,
  FaUserPlus,
  FaUserTag,
  FaFolder,
  FaProjectDiagram,
  FaClipboardList,
  FaClock,
  FaCheckCircle,
  FaCheck,
  FaSpinner,
  FaCheckDouble,
  FaMoneyBillWave,
  FaCreditCard,
  FaPlus,
  FaExclamationTriangle,
  FaUndo,
  FaFileAlt,
  FaHourglassHalf,
  FaHandshake,
  FaSearch,
  FaPlay,
  FaTasks,
  FaDollarSign,
  FaReceipt,
  FaChartLine,
  FaChartBar,
  FaCommentDots,
  FaChartPie,
  FaStream,
  FaListOl,
  FaUserCircle,
  FaLanguage,
  FaCoins,
  FaBan,
  FaPlug,
  FaBuilding,
  FaTools,
  FaTags,
  FaUsersCog,
  FaEnvelope,
  FaLockOpen,
  FaExchangeAlt,
  FaStopwatch,
} from "react-icons/fa";
import { getFirestore, doc } from "firebase/firestore";
import { safeOnSnapshot } from "../lib/safeSnapshot";

const Header = () => {
  const { t } = useTranslation();
  const { logout, user, loading } = useAuth();
  const {
    masterUnreadCount,
    budgetCount,
    approvalCount,
    inAnalysisCount,
    onGoingCount,
    paymentsCount,
    refundsCount,
    clientUnreadCount,
    clientBudgetCount,
    clientBudgetReadyCount,
    clientDivergenceCount,
    clientAnalysisCount,
    clientGoingOnCount,
    clientPaymentsCount,
    clientDraftsCount,
    clientCompletedCount,
    clientRefundCount,
  } = useNotifications();

  // Hooks centralizados para carregar notificações
  useClientNotifications(); // Para clientes
  useMasterNotifications(); // Para master
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProjectsMenuOpen, setIsProjectsMenuOpen] = useState(true);
  const [isEmployeesMenuOpen, setIsEmployeesMenuOpen] = useState(false);
  const [isFilesMenuOpen, setIsFilesMenuOpen] = useState(false);
  const [isWorkflowMenuOpen, setIsWorkflowMenuOpen] = useState(false);
  const [isDashboardsMenuOpen, setIsDashboardsMenuOpen] = useState(true);
  const [isClientAreaOpen, setIsClientAreaOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const [liveCredits, setLiveCredits] = useState(user?.credits || 0);
  const [hasApiKey, setHasApiKey] = useState(false);
  // Abas liberadas para o CARGO ATIVO do colaborador (multi-cargo: o cargo
  // ativo é escolhido no seletor do header). Define o menu lateral.
  const { activeCargo } = useActiveCargo();
  const employeeCargo = activeCargo || user?.employeeRole;
  const { tabs: employeeTabs } = useRoleTabs(employeeCargo, permsForRole(user, employeeCargo));

  // PERFIL ATIVO = ÁREA da URL. Para contas multi-papel, o menu lateral segue a
  // área em que o usuário está (não fica preso no papel de maior prioridade).
  // Fora das áreas conhecidas, cai na prioridade por papel (hasRole) — antigo.
  const currentArea = location.pathname.startsWith("/company/master")
    ? "master"
    : location.pathname.startsWith("/employee")
    ? "employee"
    : location.pathname.startsWith("/client")
    ? "client"
    : null;
  const viewIsMaster = currentArea
    ? currentArea === "master"
    : hasRole(user?.userType, "master");
  const viewIsEmployee = currentArea
    ? currentArea === "employee"
    : hasRole(user?.userType, "employee");

  // Atualizar créditos em tempo real
  useEffect(() => {
    if (!user?.uid) return;
    const firestore = getFirestore();
    const unsub = safeOnSnapshot(
      doc(firestore, "users", user.uid),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setLiveCredits(Number(data.credits) || 0);
          setHasApiKey(Boolean(data.apiKey));
        }
      },
      { label: "users-doc" }
    );
    return () => unsub();
  }, [user?.uid]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const isPublicRoute = () => {
    if (!user || !user.userType) {
      return true;
    }
    const publicRoutes = ["/", "/login", "/register"];
    return publicRoutes.includes(location.pathname);
  };

  const toggleSidebar = () => {
    const newState = !isSidebarExpanded;
    setIsSidebarExpanded(newState);

    // Salvar estado no localStorage para sincronizar com ClientLayout
    localStorage.setItem("sidebarExpanded", JSON.stringify(newState));

    // Disparar evento customizado para notificar mudanças
    window.dispatchEvent(
      new CustomEvent("sidebarToggle", { detail: newState })
    );
  };

  const toggleProjectsMenu = () => {
    setIsProjectsMenuOpen(!isProjectsMenuOpen);
  };

  const toggleEmployeesMenu = () => {
    setIsEmployeesMenuOpen(!isEmployeesMenuOpen);
  };

  const toggleFilesMenu = () => {
    setIsFilesMenuOpen(!isFilesMenuOpen);
  };

  const toggleWorkflowMenu = () => {
    setIsWorkflowMenuOpen(!isWorkflowMenuOpen);
  };

  // Função para lidar com cliques nos links do sidebar
  const handleLinkClick = (event, path) => {
    // Se o sidebar não está mostrando texto (está minimizado), apenas expandir
    if (!shouldShowText()) {
      event.preventDefault();
      setIsSidebarExpanded(true);

      // Salvar estado no localStorage para sincronizar com ClientLayout/MasterLayout
      localStorage.setItem("sidebarExpanded", JSON.stringify(true));

      // Disparar evento customizado para notificar mudanças
      window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: true }));
      return;
    }
    // Se está mostrando texto, permitir navegação normal
  };

  // Função para lidar com cliques no botão "Projetos"
  const handleProjectsClick = () => {
    // Se o sidebar não está mostrando texto (está minimizado), apenas expandir
    if (!shouldShowText()) {
      setIsSidebarExpanded(true);

      // Salvar estado no localStorage para sincronizar com ClientLayout/MasterLayout
      localStorage.setItem("sidebarExpanded", JSON.stringify(true));

      // Disparar evento customizado para notificar mudanças
      window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: true }));
      return;
    }
    // Se está mostrando texto, fazer toggle do menu projetos
    toggleProjectsMenu();
  };

  // Função para lidar com cliques no botão "Colaboradores"
  const handleEmployeesClick = () => {
    if (!shouldShowText()) {
      setIsSidebarExpanded(true);
      localStorage.setItem("sidebarExpanded", JSON.stringify(true));
      window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: true }));
      return;
    }
    toggleEmployeesMenu();
  };

  // Função para lidar com cliques no botão "Arquivos"
  const handleFilesClick = () => {
    if (!shouldShowText()) {
      setIsSidebarExpanded(true);
      localStorage.setItem("sidebarExpanded", JSON.stringify(true));
      window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: true }));
      return;
    }
    toggleFilesMenu();
  };

  // Função para lidar com cliques no botão "Dashboards"
  const handleDashboardsClick = () => {
    if (!shouldShowText()) {
      setIsSidebarExpanded(true);
      localStorage.setItem("sidebarExpanded", JSON.stringify(true));
      window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: true }));
      return;
    }
    setIsDashboardsMenuOpen((prev) => !prev);
  };

  // Função para lidar com cliques no botão "Workflow"
  const handleWorkflowClick = () => {
    if (!shouldShowText()) {
      setIsSidebarExpanded(true);
      localStorage.setItem("sidebarExpanded", JSON.stringify(true));
      window.dispatchEvent(new CustomEvent("sidebarToggle", { detail: true }));
      return;
    }
    toggleWorkflowMenu();
  };

  // Função para mapear ícones baseado na URL específica
  const getIconForPath = (path) => {
    const iconMap = {
      // Links principais
      "/company/master/dashboard": (
        <FaHome
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/client/dashboard": (
        <FaHome
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/clients": (
        <FaUsers
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/employees": (
        <FaUserTie
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/employees/register": (
        <FaUserPlus
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/employees/types": (
        <FaUserTag
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/employees/manage": (
        <FaUsersCog
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/email-templates": (
        <FaEnvelope
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/employee-payments": (
        <FaMoneyBillWave
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/files/all": (
        <FaFileAlt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/files/ongoing": (
        <FaSpinner
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/files/done": (
        <FaCheckDouble
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/files/observations": (
        <FaCommentDots
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/files/productivity": (
        <FaChartBar
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/activity-logs": (
        <FaClipboardList
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/profile": (
        <FaUserCircle
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),

      // Links de projetos - Master
      "/company/master/projects": (
        <FaTasks
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-budget": (
        <FaClock
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-approval": (
        <FaHandshake
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-approved": (
        <FaCheck
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-in-analysis": (
        <FaSearch
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/ongoing": (
        <FaPlay
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-done": (
        <FaCheckDouble
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-paid": (
        <FaMoneyBillWave
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/projects-cancelled": (
        <FaBan
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/pending-release": (
        <FaLockOpen
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/pending-release": (
        <FaLockOpen
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/payments": (
        <FaCreditCard
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/refunds": (
        <FaUndo
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/add-project": (
        <FaPlus
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/drafts": (
        <FaFileAlt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),

      // Links de projetos - Client
      "/client/projects": (
        <FaTasks
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects/clientaddproject": (
        <FaPlus
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-drafts": (
        <FaFileAlt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-budget": (
        <FaHourglassHalf
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-budget-received": (
        <FaReceipt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-divergence": (
        <FaExclamationTriangle
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-analysis": (
        <FaSearch
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/going-on": (
        <FaSpinner
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-done": (
        <FaCheckCircle
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/payments": (
        <FaDollarSign
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-paid": (
        <FaChartLine
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/projects-refund": (
        <FaUndo
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/profile": (
        <FaUserCircle
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/add-collaborator": (
        <FaUserTie
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/client/integrations": (
        <FaPlug
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      // Sublinks de Empresa — sem entrada aqui, todos caíam no ícone genérico
      // (FaBox) e ficavam iguais no sidebar.
      "/company/master/currencies": (
        <FaExchangeAlt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/languages": (
        <FaLanguage
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/deadlines": (
        <FaStopwatch
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/affiliates": (
        <FaHandshake
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/manage-files": (
        <FaFileAlt
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/andamento": (
        <FaProjectDiagram
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/employee-performance": (
        <FaChartBar
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/andamento-arquivos": (
        <FaStream
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/job-queue": (
        <FaListOl
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/task-management": (
        <FaTasks
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/credits": (
        <FaCoins
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/workflow": (
        <FaProjectDiagram
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/translation-statuses": (
        <FaTags
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/company/master/workflow/templates": (
        <FaProjectDiagram
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/company/master/workflow/start": (
        <FaPlay
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      // --- Colaborador: links de topo (ícone maior, igual ao master) ---
      "/employee/files": (
        <FaTasks
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/employee/dashboard": (
        <FaChartPie
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      "/employee/performance": (
        <FaChartLine
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      ),
      // --- Colaborador: sublinks (ícone menor, espelhando o master) ---
      "/employee/projects": (
        <FaTasks
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/drafts": (
        <FaFileAlt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/projects-budget": (
        <FaClock
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/projects-approval": (
        <FaHandshake
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/projects-in-analysis": (
        <FaSearch
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/ongoing": (
        <FaPlay
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/projects-done": (
        <FaCheckDouble
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/projects-paid": (
        <FaMoneyBillWave
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/projects-cancelled": (
        <FaBan
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/payments": (
        <FaCreditCard
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/refunds": (
        <FaUndo
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/add-project": (
        <FaPlus
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/all-files": (
        <FaFileAlt
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/andamento": (
        <FaChartLine
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/andamento-arquivos": (
        <FaStream
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/job-queue": (
        <FaListOl
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/task-management": (
        <FaTasks
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/files-ongoing": (
        <FaSpinner
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/employee-performance": (
        <FaChartBar
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/productivity": (
        <FaChartBar
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/done-files": (
        <FaCheckDouble
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
      "/employee/workflow": (
        <FaProjectDiagram
          style={{
            width: "clamp(0.75rem, 1vw, 1rem)",
            height: "clamp(0.75rem, 1vw, 1rem)",
          }}
          className="text-gray-500"
        />
      ),
    };

    return (
      iconMap[path] || (
        <FaBox
          style={{
            width: "clamp(0.875rem, 1.25vw, 1.25rem)",
            height: "clamp(0.875rem, 1.25vw, 1.25rem)",
          }}
          className="text-gray-600"
        />
      )
    );
  };

  // Fechar dropdown e sidebar quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Fechar user menu quando clicar fora
      if (isUserMenuOpen && !event.target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }

      // Fechar client area dropdown quando clicar fora
      if (isClientAreaOpen && !event.target.closest(".client-area-container")) {
        setIsClientAreaOpen(false);
      }

      // Fechar sidebar quando clicar fora (apenas quando expandido e não for mobile)
      if (
        isDesktop &&
        isSidebarExpanded &&
        !event.target.closest(".sidebar-container") &&
        !event.target.closest(".sidebar-toggle-btn")
      ) {
        setIsSidebarExpanded(false);
        localStorage.setItem("sidebarExpanded", JSON.stringify(false));
        window.dispatchEvent(
          new CustomEvent("sidebarToggle", { detail: false })
        );
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isUserMenuOpen,
    isSidebarExpanded,
    isDesktop,
    isClientAreaOpen,
  ]);

  // Auto-expandir sidebar em tablet para melhor usabilidade - COMENTADO para permitir controle manual
  // useEffect(() => {
  //   if (isTablet) {
  //     setIsSidebarExpanded(true);
  //   }
  // }, [isTablet]);

  // Inicializar estado do sidebar do localStorage
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarExpanded");
    if (savedState !== null) {
      setIsSidebarExpanded(JSON.parse(savedState));
    }
  }, []);

  // Links específicos por tipo de usuário para o menu "Projetos"
  const getProjectLinks = () => {
    const isMaster = hasRole(user?.userType, "master");

    if (isMaster) {
      return [
        {
          to: "/company/master/projects",
          label: t("projects.all"),
          notificationCount: masterUnreadCount,
        },

        {
          to: "/company/master/drafts",
          label: t("projects.drafts"),
        },
        {
          to: "/company/master/projects-budget",
          label: t("projects.awaitingBudget"),
          notificationCount: budgetCount,
        },
        {
          to: "/company/master/projects-approval",
          label: t("master.awaitingApproval"),
          notificationCount: approvalCount,
        },
        {
          to: "/company/master/projects-in-analysis",
          label: t("master.inAnalysis"),
          notificationCount: inAnalysisCount,
        },
        {
          to: "/company/master/ongoing",
          label: t("master.ongoing"),
          notificationCount: onGoingCount,
        },
        { to: "/company/master/projects-done", label: t("projects.completed") },
        { to: "/company/master/pending-release", label: t("master.pendingRelease") },
        { to: "/company/master/projects-paid", label: t("projects.paid") },
        { to: "/company/master/projects-cancelled", label: t("projects.cancelled") },
        { to: "/company/master/payments", label: t("master.payments") },
        { to: "/company/master/refunds", label: t("master.refunds") },
        { to: "/company/master/add-project", label: t("master.addProject") },
      ];
    }
    // Links para clientes (B2B, B2C, Colab)
    return [
      {
        to: "/client/projects",
        label: t("projects.all"),
        notificationCount: clientUnreadCount,
      },
      {
        to: "/client/projects-drafts",
        label: t("projects.drafts"),
        notificationCount: clientDraftsCount,
      },
      {
        to: "/client/projects-budget",
        label: t("projects.awaitingBudget"),
        notificationCount: clientBudgetCount,
      },
      {
        to: "/client/projects-budget-received",
        label: t("projects.budgetReceived"),
        notificationCount: clientBudgetReadyCount,
      },
      {
        to: "/client/projects-divergence",
        label: t("projects.inDivergence"),
        notificationCount: clientDivergenceCount,
      },
      {
        to: "/client/projects-analysis",
        label: t("projects.inAnalysis"),
        notificationCount: clientAnalysisCount,
      },
      {
        to: "/client/going-on",
        label: t("projects.inProgress"),
        notificationCount: clientGoingOnCount,
      },
      {
        to: "/client/projects-done",
        label: t("projects.completed"),
        notificationCount: clientCompletedCount,
      },
      {
        to: "/client/payments",
        label: t("projects.pendingPayments"),
        notificationCount: clientPaymentsCount,
      },
      { to: "/client/projects-paid", label: t("projects.paid") },
      {
        to: "/client/projects-refund",
        label: t("projects.refund"),
        notificationCount: clientRefundCount,
      },
    ];
  };

  // Links principais de navegação (não são projetos)
  const getMainLinks = () => {
    const isMaster = viewIsMaster;
    const isEmployee = viewIsEmployee;

    if (isEmployee) {
      // As abas vêm da config do CARGO (employee_types.tabs), resolvida pelo
      // hook useRoleTabs. Nenhuma regra por nome de cargo — qualquer cargo
      // criado pelo Master monta seu menu a partir das abas que ele liberou.
      return employeeTabs.map((tab) => ({ to: tab.route, label: tab.label }));
    }

    const navLinks = [
      { to: "/company/master/dashboard", label: t("common.dashboard") },
    ];

    if (isMaster) {
      navLinks.push(
        { to: "/company/master/clients", label: t("master.clients") },
        { to: "/company/master/workflow", label: t("master.workflow") },
        { to: "/company/master/translation-statuses", label: "Status de Tradução" },
        { to: "/company/master/credits", label: "Créditos" },
        {
          to: "/company/master/activity-logs",
          label: t("master.activityLogs"),
        },
        { to: "/company/master/manage-files", label: "Gerenciar Capa" },
        { to: "/company/master/affiliates", label: t("master.affiliates") },
        { to: "/company/master/profile", label: t("common.profile") }
      );
    }

    return navLinks;
  };

  // Sublinks do menu "Colaboradores" (apenas master)
  const getEmployeeLinks = () => [
    {
      to: "/company/master/employees/register",
      label: t("master.employeesRegister"),
    },
    {
      to: "/company/master/employees/types",
      label: t("master.employeesTypes"),
    },
    {
      to: "/company/master/employees/manage",
      label: t("master.employeesManage"),
    },
  ];

  // Sublinks do menu "Arquivos" (apenas master)
  const getFileLinks = () => [
    {
      to: "/company/master/files/all",
      label: t("master.filesAll"),
    },
    {
      to: "/company/master/files/ongoing",
      label: t("master.filesOngoing"),
    },
    {
      to: "/company/master/files/done",
      label: t("master.filesDone"),
    },
    {
      to: "/company/master/files/productivity",
      label: t("master.filesProductivity"),
    },
    {
      to: "/company/master/files/observations",
      label: t("master.filesObservations"),
    },
  ];

  // Sublinks dos menus do Resource Manager
  const getRmProjectLinks = () => [
    { to: "/employee/ongoing", label: "Em Andamento" },
  ];
  const getRmFileLinks = () => [
    { to: "/employee/all-files", label: "Todos Arquivos" },
    { to: "/employee/done-files", label: "Arquivos Concluídos" },
  ];

  // Sublinks do menu "Workflow" (apenas master)
  const getWorkflowLinks = () => [
    {
      to: "/company/master/workflow/templates",
      label: t("master.workflowTemplates"),
    },
    {
      to: "/company/master/workflow/start",
      label: t("master.workflowStart"),
    },
  ];

  // Links adicionais para clientes (perfil e colaboradores)
  const getClientAdditionalLinks = () => {
    // Papel de cliente efetivo (multi-papel não esconde o menu de cliente).
    const userType = clientRole(user?.userType, primaryRole(user?.userType));
    const links = [{ to: "/client/profile", label: t("client.myProfile") }];

    // Adicionar colaboradores se for b2b ou b2c
    if (userType === "b2b" || userType === "b2c") {
      links.push({
        to: "/client/add-collaborator",
        label: t("client.collaborators"),
      });
    }

    // Integrações — apenas se o master tiver gerado uma API Key para este cliente
    if (hasApiKey) {
      links.push({
        to: "/client/integrations",
        label: t("client.integrations"),
      });
    }

    return links;
  };

  // Função melhorada para verificar link ativo - apenas rota exata
  const isActive = (path) => {
    // Para rotas dinâmicas como /client/projects/:projectId
    if (
      path === "/client/projects" &&
      location.pathname.startsWith("/client/projects/") &&
      location.pathname !== "/client/projects/clientaddproject"
    ) {
      return true;
    }

    // Para rotas com parâmetros como /company/master/project/:id
    if (
      path === "/company/master/projects" &&
      location.pathname.startsWith("/company/master/project/")
    ) {
      return true;
    }

    // Verificação exata da rota
    return location.pathname === path;
  };

  // Determinar se o sidebar deve mostrar texto
  const shouldShowText = () => {
    return isMobile ? isSidebarExpanded : isSidebarExpanded;
  };

  if (loading) {
    return (
      <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <nav
          className="w-full"
          style={{
            paddingLeft: "clamp(1rem, 2vw, 3rem)",
            paddingRight: "clamp(1rem, 2vw, 3rem)",
          }}
        >
          <div className="flex justify-between items-center h-[64px]">
            <div className="flex items-center">
              <img
                src={logo}
                alt="Logo"
                className="h-[50px] w-auto object-contain"
              />
            </div>
            <div className="hidden sm:flex items-center space-x-4">
              <span className="text-gray-500 text-sm">
                {t("common.loading")}
              </span>
            </div>
          </div>
        </nav>
      </header>
    );
  }

  // Header para rotas públicas
  if (isPublicRoute()) {
    const homeAnchors = [
      { id: "inicio", label: t("navigation.home") },
      { id: "sobre", label: t("navigation.about") },
      // { id: "cotacao", label: "Cotação" }, // COMENTADO TEMPORARIAMENTE
      { id: "servicos", label: t("navigation.services") },
      { id: "processo", label: t("navigation.process") },
      { id: "idiomas", label: t("navigation.languages") },
      { id: "vantagens", label: t("navigation.advantages") },
    ];
    const anchorHref = (id) =>
      location.pathname === "/" ? `#${id}` : `/#${id}`;

    return (
      <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        <nav
          className="w-full"
          style={{
            paddingLeft: "clamp(1rem, 2vw, 3rem)",
            paddingRight: "clamp(1rem, 2vw, 3rem)",
          }}
        >
          <div className="grid grid-cols-[auto,1fr,auto] items-center h-[64px]">
            {/* Logo (esquerda) */}
            <div className="flex gap-4 h-full">
              <Link to="/" className="flex items-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="h-[50px] w-auto object-contain"
                />
              </Link>
            </div>

            {/* Âncoras da Home (centro) */}
            <div className="hidden sm:flex items-center justify-center space-x-3 lg:space-x-5 self-end pb-1 whitespace-nowrap overflow-hidden">
              {homeAnchors.map((a) =>
                a.isLink ? (
                  <Link
                    key={a.id}
                    to={a.to}
                    className="text-blue-600 hover:text-blue-800 px-2 py-1 sm:px-3 sm:py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    {a.label}
                  </Link>
                ) : (
                  <a
                    key={a.id}
                    href={anchorHref(a.id)}
                    className="text-gray-700 hover:text-primary px-2 py-1 sm:px-3 sm:py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    {a.label}
                  </a>
                )
              )}
            </div>

            {/* Links públicos (direita), alinhados como os demais */}
            <div className="flex items-center justify-end space-x-4 lg:space-x-6 self-end pb-1">
              <LanguageSelector />
              {/* Dropdown Área do Cliente - Desktop */}
              <div className="hidden sm:block relative client-area-container">
                <button
                  onClick={() => setIsClientAreaOpen(!isClientAreaOpen)}
                  className="text-gray-700 hover:text-primary px-2 py-1 sm:px-3 sm:py-2 rounded-md text-sm font-medium flex items-center gap-1"
                >
                  {t("client.area")}
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isClientAreaOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isClientAreaOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-md shadow-lg py-1 z-50 border">
                    <Link
                      to="/register"
                      onClick={() => setIsClientAreaOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t("common.register")}
                    </Link>
                    <Link
                      to="/login"
                      onClick={() => setIsClientAreaOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t("common.login")}
                    </Link>
                  </div>
                )}
              </div>
              <div className="sm:hidden">
                <button
                  onClick={toggleSidebar}
                  className="text-gray-700 hover:text-primary p-2 sidebar-toggle-btn"
                >
                  <FaBars className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Sidebar Mobile para páginas públicas */}
        {isSidebarExpanded && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden"
              onClick={toggleSidebar}
            />
            <div className="fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-white shadow-lg z-50 sm:hidden">
              <div className="flex justify-between items-center p-4 border-b">
                <img src={logo} alt="Logo" className="h-8 w-auto" />
                <button
                  onClick={toggleSidebar}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <nav className="flex flex-col space-y-2">
                  <div className="px-3 py-2 border-b mb-2">
                    <LanguageSelector />
                  </div>
                  {homeAnchors.map((a) =>
                    a.isLink ? (
                      <Link
                        key={a.id}
                        to={a.to}
                        onClick={toggleSidebar}
                        className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded-md text-sm font-medium"
                      >
                        {a.label}
                      </Link>
                    ) : (
                      <a
                        key={a.id}
                        href={anchorHref(a.id)}
                        onClick={toggleSidebar}
                        className="text-gray-700 hover:text-primary px-3 py-2 rounded-md text-sm font-medium"
                      >
                        {a.label}
                      </a>
                    )
                  )}
                  <div className="border-t pt-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-1">
                      {t("client.area")}
                    </div>
                    <Link
                      to="/register"
                      onClick={toggleSidebar}
                      className="text-gray-700 hover:text-primary px-3 py-2 rounded-md text-sm font-medium"
                    >
                      {t("common.register")}
                    </Link>
                    <Link
                      to="/login"
                      onClick={toggleSidebar}
                      className="text-gray-700 hover:text-primary px-3 py-2 rounded-md text-sm font-medium"
                    >
                      {t("common.login")}
                    </Link>
                  </div>
                </nav>
              </div>
            </div>
          </>
        )}
      </header>
    );
  }

  // Header para usuários autenticados
  // Para as comparações de cliente (=== "b2b"/"b2c"/"colab") usamos o papel de
  // cliente efetivo. isMaster/isEmployee aqui seguem o PERFIL ATIVO (área da
  // URL), para o menu lateral refletir o perfil escolhido em contas multi-papel.
  const userType = clientRole(user?.userType, primaryRole(user?.userType));
  const isMaster = viewIsMaster;
  const isEmployee = viewIsEmployee;
  // Colaboradores montam o menu via abas dinâmicas (employeeTabs). O antigo
  // dropdown por cargo (Resource Manager / Gerente de Projeto) foi aposentado.
  const showRmTabs = false;
  const projectLinks = getProjectLinks();
  const mainLinks = getMainLinks();

  // --- Sidebar do master: grupos por categoria (links + sublinks) ---------
  const masterEmpresaLinks = [
    { to: "/company/master/clients", label: t("master.clients") },
    { to: "/company/master/employees/register", label: "Registrar Colaborador" },
    { to: "/company/master/employees/types", label: "Tipos de Colaborador" },
    { to: "/company/master/employees/manage", label: "Gerenciar Colaboradores" },
    { to: "/company/master/employee-payments", label: "Pagamentos" },
    { to: "/company/master/email-templates", label: "Email" },
    { to: "/company/master/currencies", label: "Moedas" },
    { to: "/company/master/languages", label: "Idiomas" },
    { to: "/company/master/deadlines", label: "Prazos" },
    { to: "/company/master/affiliates", label: t("master.affiliates") },
  ];
  const masterFerramentasLinks = [
    { to: "/company/master/workflow", label: t("master.workflow") },
    { to: "/company/master/translation-statuses", label: "Status de Tradução" },
    { to: "/company/master/manage-files", label: "Gerenciar Capa" },
    { to: "/company/master/credits", label: "Créditos" },
    { to: "/company/master/activity-logs", label: t("master.activityLogs") },
  ];

  // Renderiza um grupo recolhível (dropdown) do sidebar do master.
  const renderMasterGroup = (key, label, Icon, open, onToggle, links) => {
    const groupActive = links.some((l) => isActive(l.to));
    return (
      <div key={key}>
        <button
          onClick={onToggle}
          className={`w-full flex items-center ${
            shouldShowText() ? "justify-between px-3" : "justify-center"
          } py-2.5 rounded-xl transition-all duration-200 ${
            (open && shouldShowText()) || groupActive
              ? "bg-blue-50 text-blue-700"
              : "text-slate-600 hover:bg-slate-50"
          }`}
          title={!shouldShowText() ? label : ""}
        >
          <div className="flex items-center">
            <span
              className={`flex-shrink-0 ${
                (open && shouldShowText()) || groupActive
                  ? "text-blue-600"
                  : "text-slate-400"
              }`}
            >
              <Icon
                style={{
                  width: "clamp(0.875rem, 1.25vw, 1.25rem)",
                  height: "clamp(0.875rem, 1.25vw, 1.25rem)",
                }}
              />
            </span>
            {shouldShowText() && (
              <span
                className={`ml-3 text-[13px] whitespace-nowrap ${
                  open || groupActive ? "font-semibold" : "font-medium"
                }`}
              >
                {label}
              </span>
            )}
          </div>
          {shouldShowText() && (
            <FaChevronDown
              className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                open ? "rotate-0" : "-rotate-90"
              }`}
            />
          )}
        </button>
        {open && shouldShowText() && (
          <div className="ml-5 mt-2 space-y-0.5 border-l-2 border-slate-100 pl-3 py-1">
            {links.map((sublink) => (
              <Link
                key={sublink.to}
                to={sublink.to}
                onClick={(e) => handleLinkClick(e, sublink.to)}
                className={`flex items-center px-3 py-2 text-[12px] rounded-lg transition-all duration-200 ${
                  isActive(sublink.to)
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span
                  className={`flex-shrink-0 ${
                    isActive(sublink.to) ? "text-blue-500" : "text-slate-400"
                  }`}
                >
                  {getIconForPath(sublink.to)}
                </span>
                <span className="ml-2.5 whitespace-nowrap">{sublink.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Estilos customizados para scrollbar - aparece apenas quando necessário */}
      <style>{`
        .sidebar-container {
          overflow-y: auto;
          overflow-x: hidden;
        }
        /* Estilos para navegadores WebKit (Chrome, Safari, Edge) */
        .sidebar-container::-webkit-scrollbar {
          width: 5px;
        }
        .sidebar-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 4px;
        }
        .sidebar-container:hover::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.5);
        }
        .sidebar-container::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.7);
        }
        /* Firefox */
        .sidebar-container {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }
        .sidebar-container:hover {
          scrollbar-color: rgba(148, 163, 184, 0.5) transparent;
        }
      `}</style>
      {/* Header Fixo */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm border-b border-slate-200 z-50" style={{ width: "100%", maxWidth: "100vw" }}>
        <nav
          className="w-full"
          style={{
            paddingLeft: "clamp(1rem, 2vw, 2.5rem)",
            paddingRight: "clamp(1rem, 2vw, 2.5rem)",
            maxWidth: "100%",
          }}
        >
          <div className="flex justify-between items-center h-[64px]">
            {/* Menu + Logo */}
            <div className="flex items-center gap-1">
              <button
                onClick={toggleSidebar}
                className="p-2.5 rounded-lg transition-all duration-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 sidebar-toggle-btn"
                title={isSidebarExpanded ? "Fechar Menu" : "Abrir Menu"}
              >
                <FaBars className="w-[18px] h-[18px]" />
              </button>
              <Link
                to={
                  isMaster ? "/company/master/dashboard" : "/client/dashboard"
                }
                className="flex items-center ml-1"
              >
                <img
                  src={logo}
                  alt="Logo"
                  className="h-[42px] w-auto object-contain"
                />
              </Link>
            </div>

            {/* Menu de Configuração */}
            <div className="relative user-menu-container flex items-center gap-3">
              {/* Créditos do usuário, se houver - apenas no desktop */}
              {liveCredits > 0 && !isMobile && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full">
                  <span className="text-emerald-600 font-semibold text-xs">
                    $ {liveCredits}
                  </span>
                  <span className="text-emerald-500 text-xs hidden sm:inline">
                    {t("common.credits")}
                  </span>
                </div>
              )}
              <LanguageSelector />
              {/* Sino para todos os usuários autenticados (colaborador, master e cliente). */}
              <NotificationBell />
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 ${isUserMenuOpen ? "bg-slate-200 text-slate-600" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"}`}
                title="Menu do Usuário"
              >
                <FaCog className="w-[18px] h-[18px]" />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg py-2 z-50 border border-slate-100">
                  <div className="px-3 py-2 border-b border-slate-100 mb-1">
                    <p className="text-xs text-slate-400 font-medium">{t("common.loggedAs") || "Logado como"}</p>
                    <p className="text-sm text-slate-700 font-medium truncate">{user?.nomeCompleto || user?.email}</p>
                  </div>

                  <CargoSwitcher onNavigate={() => setIsUserMenuOpen(false)} />
                  <ProfileSwitcher onNavigate={() => setIsUserMenuOpen(false)} />

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors duration-200"
                  >
                    <FaSignOutAlt className="w-4 h-4 text-slate-400 mr-2.5" />
                    <span>{t("common.logout")}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Sidebar */}
      {(isMobile ? isSidebarExpanded : true) && (
        <aside
          className={`sidebar-container fixed top-[64px] left-0 bottom-0 bg-white border-r border-slate-200 shadow-sm z-40 transition-[width] duration-200 overflow-y-auto max-w-[85vw] ${
            isMobile ? "w-72" : isSidebarExpanded ? "w-72" : "w-16"
          }`}
        >
          {/* Nome do usuário no topo do sidebar */}
          {shouldShowText() && (
            <div className="w-full py-4 px-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                  <span className="text-white font-semibold text-sm">
                    {(user?.nomeCompleto || user?.email || "U").charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-slate-800 text-sm font-semibold truncate block">
                    {user?.nomeCompleto || user?.email}
                  </span>
                  {/* Créditos do usuário no sidebar - apenas no mobile */}
                  {liveCredits > 0 && isMobile && (
                    <span className="text-emerald-600 font-medium text-xs mt-0.5 block">
                      $ {liveCredits} {t("common.credits")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col h-full">
            <nav className="flex-1 py-4">
              <div className="px-3 space-y-1">
                {/* Dashboard - sempre primeiro (no master vai para o grupo "Dashboards" abaixo) */}
                {mainLinks
                  .filter((link) => link.to.includes("dashboard") && !isMaster)
                  .map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={(e) => handleLinkClick(e, link.to)}
                      className={`flex items-center ${
                        shouldShowText()
                          ? "px-3"
                          : "justify-center"
                      } py-2.5 rounded-xl transition-all duration-200 ${
                        isActive(link.to)
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      title={!shouldShowText() ? link.label : ""}
                    >
                      <span className={`flex-shrink-0 ${isActive(link.to) ? "text-blue-600" : "text-slate-400"}`}>
                        {getIconForPath(link.to)}
                      </span>
                      {shouldShowText() && (
                        <span className={`ml-3 text-[13px] whitespace-nowrap ${isActive(link.to) ? "font-semibold" : "font-medium"}`}>
                          {link.label}
                        </span>
                      )}
                    </Link>
                  ))}

                {/* Colaborador: sidebar AGRUPADO no mesmo padrão do master
                    (link + sublink). As abas liberadas para o cargo ativo são
                    distribuídas nos grupos Projetos / Arquivos / Ferramentas;
                    as abas pessoais (Meus Arquivos, Desempenho) ficam soltas. */}
                {isEmployee && (() => {
                  const byKey = {};
                  employeeTabs.forEach((tb) => { byKey[tb.key] = tb; });
                  const has = (k) => !!byKey[k];
                  const sub = (keys) =>
                    keys.filter(has).map((k) => ({ to: byKey[k].route, label: byKey[k].label }));
                  const renderFlat = (k) => {
                    const tb = byKey[k];
                    if (!tb) return null;
                    return (
                      <Link
                        key={tb.route}
                        to={tb.route}
                        onClick={(e) => handleLinkClick(e, tb.route)}
                        className={`flex items-center ${
                          shouldShowText() ? "px-3" : "justify-center"
                        } py-2.5 rounded-xl transition-all duration-200 ${
                          isActive(tb.route)
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                        title={!shouldShowText() ? tb.label : ""}
                      >
                        <span className={`flex-shrink-0 ${isActive(tb.route) ? "text-blue-600" : "text-slate-400"}`}>
                          {getIconForPath(tb.route)}
                        </span>
                        {shouldShowText() && (
                          <span className={`ml-3 text-[13px] whitespace-nowrap ${isActive(tb.route) ? "font-semibold" : "font-medium"}`}>
                            {tb.label}
                          </span>
                        )}
                      </Link>
                    );
                  };
                  const projectsSub = sub([
                    "projects", "drafts", "projects-budget", "projects-approval",
                    "projects-in-analysis", "ongoing", "projects-done", "projects-paid",
                    "projects-cancelled", "payments", "refunds", "add-project",
                  ]);
                  const filesSub = sub([
                    "all-files", "files-ongoing", "andamento", "andamento-arquivos", "job-queue",
                    "task-management", "employee-performance", "productivity", "done-files",
                  ]);
                  const toolsSub = sub(["workflow"]);
                  return (
                    <>
                      {renderFlat("files")}
                      {renderFlat("performance")}
                      {projectsSub.length > 0 &&
                        renderMasterGroup("emp-projects", t("common.projects"), FaBox, isProjectsMenuOpen, handleProjectsClick, projectsSub)}
                      {filesSub.length > 0 &&
                        renderMasterGroup("emp-files", t("master.files"), FaFolder, isFilesMenuOpen, handleFilesClick, filesSub)}
                      {toolsSub.length > 0 &&
                        renderMasterGroup("emp-tools", "Ferramentas", FaTools, isWorkflowMenuOpen, handleWorkflowClick, toolsSub)}
                    </>
                  );
                })()}

                {/* Lista plana de abas dos clientes (b2b/b2c/colab não têm
                    mainLinks fora do dashboard; bloco mantido por segurança). */}
                {!isMaster && !isEmployee &&
                  mainLinks
                    .filter((link) => !link.to.includes("dashboard"))
                    .map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={(e) => handleLinkClick(e, link.to)}
                        className={`flex items-center ${
                          shouldShowText()
                            ? "px-3"
                            : "justify-center"
                        } py-2.5 rounded-xl transition-all duration-200 ${
                          isActive(link.to)
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                        title={!shouldShowText() ? link.label : ""}
                      >
                        <span className={`flex-shrink-0 ${isActive(link.to) ? "text-blue-600" : "text-slate-400"}`}>
                          {getIconForPath(link.to)}
                        </span>
                        {shouldShowText() && (
                          <span className={`ml-3 text-[13px] whitespace-nowrap ${isActive(link.to) ? "font-semibold" : "font-medium"}`}>
                            {link.label}
                          </span>
                        )}
                      </Link>
                    ))}

                {/* Menus dropdown (Resource Manager e Gerente de Projeto): Projeto / Arquivos */}
                {showRmTabs && (() => {
                  const rmProjectLinks = getRmProjectLinks();
                  const rmFileLinks = getRmFileLinks();
                  const isProjectActive = rmProjectLinks.some((l) => isActive(l.to));
                  const isFilesActive = rmFileLinks.some((l) => isActive(l.to));

                  const renderGroup = (key, label, Icon, open, onToggle, links, active) => (
                    <div key={key}>
                      <button
                        onClick={onToggle}
                        className={`w-full flex items-center ${
                          shouldShowText() ? "justify-between px-3" : "justify-center"
                        } py-2.5 rounded-xl transition-all duration-200 ${
                          (open && shouldShowText()) || active
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                        title={!shouldShowText() ? label : ""}
                      >
                        <div className="flex items-center">
                          <span
                            className={`flex-shrink-0 ${
                              (open && shouldShowText()) || active
                                ? "text-blue-600"
                                : "text-slate-400"
                            }`}
                          >
                            <Icon
                              style={{
                                width: "clamp(0.875rem, 1.25vw, 1.25rem)",
                                height: "clamp(0.875rem, 1.25vw, 1.25rem)",
                              }}
                            />
                          </span>
                          {shouldShowText() && (
                            <span
                              className={`ml-3 text-[13px] whitespace-nowrap ${
                                open || active ? "font-semibold" : "font-medium"
                              }`}
                            >
                              {label}
                            </span>
                          )}
                        </div>
                        {shouldShowText() && (
                          <FaChevronDown
                            className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                              open ? "rotate-0" : "-rotate-90"
                            }`}
                          />
                        )}
                      </button>

                      {open && shouldShowText() && (
                        <div className="ml-5 mt-2 space-y-0.5 border-l-2 border-slate-100 pl-3 py-1">
                          {links.map((sublink) => (
                            <Link
                              key={sublink.to}
                              to={sublink.to}
                              onClick={(e) => handleLinkClick(e, sublink.to)}
                              className={`flex items-center px-3 py-2 text-[12px] rounded-lg transition-all duration-200 ${
                                isActive(sublink.to)
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              <span
                                className={`flex-shrink-0 ${
                                  isActive(sublink.to) ? "text-blue-500" : "text-slate-400"
                                }`}
                              >
                                {getIconForPath(sublink.to)}
                              </span>
                              <span className="ml-2.5 whitespace-nowrap">{sublink.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );

                  return (
                    <>
                      {renderGroup(
                        "rm-project-dropdown",
                        "Projeto",
                        FaProjectDiagram,
                        isProjectsMenuOpen,
                        handleProjectsClick,
                        rmProjectLinks,
                        isProjectActive
                      )}
                      {renderGroup(
                        "rm-files-dropdown",
                        "Arquivos",
                        FaFileAlt,
                        isFilesMenuOpen,
                        handleFilesClick,
                        rmFileLinks,
                        isFilesActive
                      )}
                    </>
                  );
                })()}

                {/* Criar Projeto - para b2b, b2c e colab */}
                {!isMaster &&
                  (userType === "b2b" ||
                    userType === "b2c" ||
                    userType === "colab") && (
                    <Link
                      to="/client/projects/clientaddproject"
                      onClick={(e) =>
                        handleLinkClick(e, "/client/projects/clientaddproject")
                      }
                      className={`flex items-center ${
                        shouldShowText()
                          ? "px-3"
                          : "justify-center"
                      } py-2.5 rounded-xl transition-all duration-200 ${
                        isActive("/client/projects/clientaddproject")
                          ? "bg-emerald-50 text-emerald-700"
                          : "text-slate-600 hover:bg-emerald-50/50"
                      }`}
                      title={
                        !shouldShowText() ? t("projects.createProject") : ""
                      }
                    >
                      <span className={`flex-shrink-0 ${isActive("/client/projects/clientaddproject") ? "text-emerald-600" : "text-slate-400"}`}>
                        {getIconForPath("/client/projects/clientaddproject")}
                      </span>
                      {shouldShowText() && (
                        <span className={`ml-3 text-[13px] whitespace-nowrap ${isActive("/client/projects/clientaddproject") ? "font-semibold" : "font-medium"}`}>
                          {t("projects.createProject")}
                        </span>
                      )}
                    </Link>
                  )}

                {/* Grupo Dashboards (master) — acima de Projetos */}
                {isMaster &&
                  renderMasterGroup(
                    "master-dashboards",
                    "Dashboards",
                    FaChartLine,
                    isDashboardsMenuOpen,
                    handleDashboardsClick,
                    [
                      { to: "/company/master/dashboard", label: "Financeiro" },
                      { to: "/company/master/employee-performance", label: "Desempenho" },
                    ]
                  )}

                {/* Separador antes do menu Projetos */}
                {!isEmployee && shouldShowText() && (
                  <div className="my-3 mx-3">
                    <div className="h-px bg-slate-100"></div>
                  </div>
                )}

                {/* Menu Projetos - terceiro (oculto para employee) */}
                {!isEmployee && (
                <>
                <button
                  onClick={handleProjectsClick}
                  className={`w-full flex items-center ${
                    shouldShowText()
                      ? "justify-between px-3"
                      : "justify-center"
                  } py-2.5 rounded-xl transition-all duration-200 ${
                    isProjectsMenuOpen && shouldShowText()
                      ? "bg-violet-50 text-violet-700"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  title={!shouldShowText() ? "Projetos" : ""}
                >
                  <div className="flex items-center">
                    <div className="relative">
                      <FaBox
                        style={{
                          width: "clamp(0.875rem, 1.25vw, 1.25rem)",
                          height: "clamp(0.875rem, 1.25vw, 1.25rem)",
                        }}
                        className={`flex-shrink-0 ${isProjectsMenuOpen && shouldShowText() ? "text-violet-600" : "text-slate-400"}`}
                      />
                      {/* Badge de notificação no ícone quando sidebar está minimizado */}
                      {!shouldShowText() &&
                        isMaster &&
                        budgetCount +
                          approvalCount +
                          inAnalysisCount +
                          onGoingCount >
                          0 && (
                          <span className="absolute -top-1 -right-1 bg-rose-400 text-white text-[8px] font-bold px-1 py-0.5 rounded-full min-w-[14px] h-3.5 flex items-center justify-center text-center">
                            {budgetCount +
                              approvalCount +
                              inAnalysisCount +
                              onGoingCount >
                            9
                              ? "9+"
                              : budgetCount +
                                approvalCount +
                                inAnalysisCount +
                                onGoingCount}
                          </span>
                        )}
                      {/* Badge de notificação para clientes (sidebar minimizado).
                          clientUnreadCount = TODOS os não lidos (já engloba as
                          subcategorias). NÃO somar as subcategorias por cima —
                          isso contava o mesmo projeto em dobro. */}
                      {!shouldShowText() &&
                        (userType === "b2b" ||
                          userType === "b2c" ||
                          userType === "colab") &&
                        clientUnreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-rose-400 text-white text-[8px] font-bold px-1 py-0.5 rounded-full min-w-[14px] h-3.5 flex items-center justify-center text-center">
                            {clientUnreadCount > 9 ? "9+" : clientUnreadCount}
                          </span>
                        )}
                    </div>
                    {shouldShowText() && (
                      <span className={`ml-3 text-[13px] whitespace-nowrap ${isProjectsMenuOpen ? "font-semibold" : "font-medium"}`}>
                        {t("common.projects")}
                      </span>
                    )}
                  </div>
                  {shouldShowText() && (
                    <div className="flex items-center gap-2">
                      {/* Badge de notificação quando sidebar está expandido */}
                      {isMaster &&
                        budgetCount +
                          approvalCount +
                          inAnalysisCount +
                          onGoingCount >
                          0 && (
                          <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                            {budgetCount +
                              approvalCount +
                              inAnalysisCount +
                              onGoingCount >
                            99
                              ? "99+"
                              : budgetCount +
                                approvalCount +
                                inAnalysisCount +
                                onGoingCount}
                          </span>
                        )}
                      {/* Badge de notificação para clientes (sidebar expandido).
                          Só clientUnreadCount (todos os não lidos) — sem somar as
                          subcategorias por cima, que causava dupla contagem. */}
                      {(userType === "b2b" ||
                        userType === "b2c" ||
                        userType === "colab") &&
                        clientUnreadCount > 0 && (
                          <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                            {clientUnreadCount > 99 ? "99+" : clientUnreadCount}
                          </span>
                        )}
                      <FaChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isProjectsMenuOpen ? "rotate-0" : "-rotate-90"}`} />
                    </div>
                  )}
                </button>

                {/* Sublinks dos Projetos */}
                {isProjectsMenuOpen && shouldShowText() && (
                  <div className="ml-5 mt-2 space-y-0.5 border-l-2 border-slate-100 pl-3 py-1">
                    {projectLinks.map((link) => {
                      // Determinar cor da notificação baseada na rota
                      let notificationColor = "bg-red-500";
                      let notificationCount = link.notificationCount || 0;

                      if (isMaster) {
                        if (link.to === "/company/master/projects") {
                          // Todos Projetos - Amarela (masterUnreadCount)
                          notificationColor = "bg-yellow-500";
                          notificationCount = masterUnreadCount;
                        } else if (link.to === "/company/master/drafts") {
                          // Rascunhos - Nada
                          notificationCount = 0;
                        } else if (
                          link.to === "/company/master/projects-budget"
                        ) {
                          // Ag. orçamento - Vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = budgetCount;
                        } else if (
                          link.to === "/company/master/projects-approval"
                        ) {
                          // Ag. Aprovação - Vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = approvalCount;
                        } else if (
                          link.to === "/company/master/projects-in-analysis"
                        ) {
                          // Em analise - Vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = inAnalysisCount;
                        } else if (link.to === "/company/master/ongoing") {
                          // Em andamento - Amarela
                          notificationColor = "bg-yellow-500";
                          notificationCount = onGoingCount;
                        } else if (
                          link.to === "/company/master/projects-done"
                        ) {
                          // Projetos concluídos - Nada
                          notificationCount = 0;
                        } else if (
                          link.to === "/company/master/projects-paid"
                        ) {
                          // Projetos pagos - Nada
                          notificationCount = 0;
                        } else if (link.to === "/company/master/payments") {
                          // Pagamentos pendentes - Verde
                          notificationColor = "bg-green-500";
                          notificationCount = paymentsCount;
                        } else if (link.to === "/company/master/refunds") {
                          // Para Reembolso - Vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = refundsCount;
                        }
                      } else if (
                        userType === "b2b" ||
                        userType === "b2c" ||
                        userType === "colab"
                      ) {
                        // Lógica de notificações para clientes
                        if (link.to === "/client/projects") {
                          // Todos Projetos - usar clientRead (mesmo que master com amarelo)
                          notificationColor = "bg-yellow-500";
                          notificationCount = clientUnreadCount;
                        } else if (link.to === "/client/projects-drafts") {
                          // Rascunhos - notificação amarela
                          notificationColor = "bg-yellow-500";
                          notificationCount = clientDraftsCount;
                        } else if (link.to === "/client/projects-budget") {
                          // Aguardando orçamento - notificação amarela
                          notificationColor = "bg-yellow-500";
                          notificationCount = clientBudgetCount;
                        } else if (
                          link.to === "/client/projects-budget-received"
                        ) {
                          // Orçamento recebido - notificação vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = clientBudgetReadyCount;
                        } else if (link.to === "/client/projects-divergence") {
                          // Em divergência - notificação vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = clientDivergenceCount;
                        } else if (link.to === "/client/projects-analysis") {
                          // Em análise - notificação azul
                          notificationColor = "bg-blue-500";
                          notificationCount = clientAnalysisCount;
                        } else if (link.to === "/client/going-on") {
                          // Em andamento - notificação azul
                          notificationColor = "bg-blue-500";
                          notificationCount = clientGoingOnCount;
                        } else if (link.to === "/client/projects-done") {
                          // Projetos concluídos - notificação verde
                          notificationColor = "bg-green-500";
                          notificationCount = clientCompletedCount;
                        } else if (link.to === "/client/payments") {
                          // Pagamentos pendentes - notificação vermelha
                          notificationColor = "bg-red-500";
                          notificationCount = clientPaymentsCount;
                        } else if (link.to === "/client/projects-refund") {
                          // Em reembolso - notificação amarela
                          notificationColor = "bg-yellow-500";
                          notificationCount = clientRefundCount;
                        }
                      }

                      // Determinar classe de cor para o badge (mais suave)
                      let badgeClass = "bg-rose-400";
                      if (notificationColor === "bg-yellow-500") {
                        badgeClass = "bg-amber-400";
                      } else if (notificationColor === "bg-green-500") {
                        badgeClass = "bg-emerald-400";
                      } else if (notificationColor === "bg-blue-500") {
                        badgeClass = "bg-sky-400";
                      }

                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={(e) => handleLinkClick(e, link.to)}
                          className={`flex items-center justify-between px-3 py-2 text-[12px] rounded-lg transition-all duration-200 ${
                            isActive(link.to)
                              ? "bg-violet-50 text-violet-700 font-medium"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center">
                            <span className={`flex-shrink-0 ${isActive(link.to) ? "text-violet-500" : "text-slate-400"}`}>
                              {getIconForPath(link.to)}
                            </span>
                            <span className="ml-2.5 whitespace-nowrap">
                              {link.label}
                            </span>
                          </div>
                          {notificationCount > 0 && (
                            <span
                              className={`${badgeClass} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center`}
                            >
                              {notificationCount > 99
                                ? "99+"
                                : notificationCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
                </>
                )}
                {/* fim do gating !isEmployee do menu Projetos */}

                {/* Separador antes dos links adicionais */}
                {!isMaster && !isEmployee && shouldShowText() && (
                  <div className="my-3 mx-3">
                    <div className="h-px bg-slate-100"></div>
                  </div>
                )}

                {/* Links Adicionais para Clientes - após projetos (Perfil e Colaboradores) */}
                {!isMaster && !isEmployee &&
                  getClientAdditionalLinks().map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={(e) => handleLinkClick(e, link.to)}
                      className={`flex items-center ${
                        shouldShowText()
                          ? "px-3"
                          : "justify-center"
                      } py-2.5 rounded-xl transition-all duration-200 ${
                        isActive(link.to)
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      title={!shouldShowText() ? link.label : ""}
                    >
                      <span className={`flex-shrink-0 ${isActive(link.to) ? "text-blue-600" : "text-slate-400"}`}>
                        {getIconForPath(link.to)}
                      </span>
                      {shouldShowText() && (
                        <span className={`ml-3 text-[13px] whitespace-nowrap ${isActive(link.to) ? "font-semibold" : "font-medium"}`}>
                          {link.label}
                        </span>
                      )}
                    </Link>
                  ))}

                {/* Grupos do master por categoria */}
                {isMaster && (
                  <>
                    {renderMasterGroup(
                      "master-files",
                      "Arquivos",
                      FaFolder,
                      isFilesMenuOpen,
                      handleFilesClick,
                      (() => {
                        const fileLinks = getFileLinks();
                        // "Andamento por Projeto" logo abaixo de "Todos Arquivos".
                        return [
                          fileLinks[0],
                          {
                            to: "/company/master/andamento",
                            label: "Andamento por Projeto",
                          },
                          {
                            to: "/company/master/andamento-arquivos",
                            label: "Andamento por Arquivo",
                          },
                          {
                            to: "/company/master/job-queue",
                            label: "Fila de Jobs",
                          },
                          {
                            to: "/company/master/task-management",
                            label: "Gestão de Tarefas",
                          },
                          ...fileLinks.slice(1),
                        ];
                      })()
                    )}
                    {renderMasterGroup(
                      "master-empresa",
                      "Empresa",
                      FaBuilding,
                      isEmployeesMenuOpen,
                      handleEmployeesClick,
                      masterEmpresaLinks
                    )}
                    {renderMasterGroup(
                      "master-tools",
                      "Ferramentas",
                      FaTools,
                      isWorkflowMenuOpen,
                      handleWorkflowClick,
                      masterFerramentasLinks
                    )}
                    <Link
                      to="/company/master/profile"
                      onClick={(e) => handleLinkClick(e, "/company/master/profile")}
                      className={`flex items-center ${
                        shouldShowText() ? "px-3" : "justify-center"
                      } py-2.5 rounded-xl transition-all duration-200 ${
                        isActive("/company/master/profile")
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      title={!shouldShowText() ? t("common.profile") : ""}
                    >
                      <span
                        className={`flex-shrink-0 ${
                          isActive("/company/master/profile")
                            ? "text-blue-600"
                            : "text-slate-400"
                        }`}
                      >
                        {getIconForPath("/company/master/profile")}
                      </span>
                      {shouldShowText() && (
                        <span
                          className={`ml-3 text-[13px] whitespace-nowrap ${
                            isActive("/company/master/profile")
                              ? "font-semibold"
                              : "font-medium"
                          }`}
                        >
                          {t("common.profile")}
                        </span>
                      )}
                    </Link>
                  </>
                )}

                {/* (bloco antigo de links soltos — substituído pelos grupos acima) */}
                {false &&
                  mainLinks
                    .filter((link) => !link.to.includes("dashboard"))
                    .flatMap((link) => {
                      const linkEl = (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={(e) => handleLinkClick(e, link.to)}
                          className={`flex items-center ${
                            shouldShowText()
                              ? "px-3"
                              : "justify-center"
                          } py-2.5 rounded-xl transition-all duration-200 ${
                            isActive(link.to)
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                          title={!shouldShowText() ? link.label : ""}
                        >
                          <span className={`flex-shrink-0 ${isActive(link.to) ? "text-blue-600" : "text-slate-400"}`}>
                            {getIconForPath(link.to)}
                          </span>
                          {shouldShowText() && (
                            <span className={`ml-3 text-[13px] whitespace-nowrap ${isActive(link.to) ? "font-semibold" : "font-medium"}`}>
                              {link.label}
                            </span>
                          )}
                        </Link>
                      );

                      if (link.to !== "/company/master/clients") {
                        return [linkEl];
                      }

                      const employeeLinks = getEmployeeLinks();
                      const isEmployeesActive = employeeLinks.some((l) =>
                        isActive(l.to)
                      );

                      const dropdownEl = (
                        <div key="employees-dropdown">
                          <button
                            onClick={handleEmployeesClick}
                            className={`w-full flex items-center ${
                              shouldShowText()
                                ? "justify-between px-3"
                                : "justify-center"
                            } py-2.5 rounded-xl transition-all duration-200 ${
                              (isEmployeesMenuOpen && shouldShowText()) ||
                              isEmployeesActive
                                ? "bg-blue-50 text-blue-700"
                                : "text-slate-600 hover:bg-slate-50"
                            }`}
                            title={!shouldShowText() ? t("master.employees") : ""}
                          >
                            <div className="flex items-center">
                              <span
                                className={`flex-shrink-0 ${
                                  (isEmployeesMenuOpen && shouldShowText()) ||
                                  isEmployeesActive
                                    ? "text-blue-600"
                                    : "text-slate-400"
                                }`}
                              >
                                <FaUserTie
                                  style={{
                                    width: "clamp(0.875rem, 1.25vw, 1.25rem)",
                                    height: "clamp(0.875rem, 1.25vw, 1.25rem)",
                                  }}
                                />
                              </span>
                              {shouldShowText() && (
                                <span
                                  className={`ml-3 text-[13px] whitespace-nowrap ${
                                    isEmployeesMenuOpen || isEmployeesActive
                                      ? "font-semibold"
                                      : "font-medium"
                                  }`}
                                >
                                  {t("master.employees")}
                                </span>
                              )}
                            </div>
                            {shouldShowText() && (
                              <FaChevronDown
                                className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                                  isEmployeesMenuOpen ? "rotate-0" : "-rotate-90"
                                }`}
                              />
                            )}
                          </button>

                          {isEmployeesMenuOpen && shouldShowText() && (
                            <div className="ml-5 mt-2 space-y-0.5 border-l-2 border-slate-100 pl-3 py-1">
                              {employeeLinks.map((sublink) => (
                                <Link
                                  key={sublink.to}
                                  to={sublink.to}
                                  onClick={(e) =>
                                    handleLinkClick(e, sublink.to)
                                  }
                                  className={`flex items-center px-3 py-2 text-[12px] rounded-lg transition-all duration-200 ${
                                    isActive(sublink.to)
                                      ? "bg-blue-50 text-blue-700 font-medium"
                                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                  }`}
                                >
                                  <span
                                    className={`flex-shrink-0 ${
                                      isActive(sublink.to)
                                        ? "text-blue-500"
                                        : "text-slate-400"
                                    }`}
                                  >
                                    {getIconForPath(sublink.to)}
                                  </span>
                                  <span className="ml-2.5 whitespace-nowrap">
                                    {sublink.label}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );

                      const fileLinks = getFileLinks();
                      const isFilesActive = fileLinks.some((l) =>
                        isActive(l.to)
                      );

                      const filesDropdownEl = (
                        <div key="files-dropdown">
                          <button
                            onClick={handleFilesClick}
                            className={`w-full flex items-center ${
                              shouldShowText()
                                ? "justify-between px-3"
                                : "justify-center"
                            } py-2.5 rounded-xl transition-all duration-200 ${
                              (isFilesMenuOpen && shouldShowText()) ||
                              isFilesActive
                                ? "bg-blue-50 text-blue-700"
                                : "text-slate-600 hover:bg-slate-50"
                            }`}
                            title={!shouldShowText() ? t("master.files") : ""}
                          >
                            <div className="flex items-center">
                              <span
                                className={`flex-shrink-0 ${
                                  (isFilesMenuOpen && shouldShowText()) ||
                                  isFilesActive
                                    ? "text-blue-600"
                                    : "text-slate-400"
                                }`}
                              >
                                <FaFolder
                                  style={{
                                    width: "clamp(0.875rem, 1.25vw, 1.25rem)",
                                    height: "clamp(0.875rem, 1.25vw, 1.25rem)",
                                  }}
                                />
                              </span>
                              {shouldShowText() && (
                                <span
                                  className={`ml-3 text-[13px] whitespace-nowrap ${
                                    isFilesMenuOpen || isFilesActive
                                      ? "font-semibold"
                                      : "font-medium"
                                  }`}
                                >
                                  {t("master.files")}
                                </span>
                              )}
                            </div>
                            {shouldShowText() && (
                              <FaChevronDown
                                className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                                  isFilesMenuOpen ? "rotate-0" : "-rotate-90"
                                }`}
                              />
                            )}
                          </button>

                          {isFilesMenuOpen && shouldShowText() && (
                            <div className="ml-5 mt-2 space-y-0.5 border-l-2 border-slate-100 pl-3 py-1">
                              {fileLinks.map((sublink) => (
                                <Link
                                  key={sublink.to}
                                  to={sublink.to}
                                  onClick={(e) =>
                                    handleLinkClick(e, sublink.to)
                                  }
                                  className={`flex items-center px-3 py-2 text-[12px] rounded-lg transition-all duration-200 ${
                                    isActive(sublink.to)
                                      ? "bg-blue-50 text-blue-700 font-medium"
                                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                  }`}
                                >
                                  <span
                                    className={`flex-shrink-0 ${
                                      isActive(sublink.to)
                                        ? "text-blue-500"
                                        : "text-slate-400"
                                    }`}
                                  >
                                    {getIconForPath(sublink.to)}
                                  </span>
                                  <span className="ml-2.5 whitespace-nowrap">
                                    {sublink.label}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );

                      return [linkEl, dropdownEl, filesDropdownEl];
                    })}
              </div>
            </nav>
          </div>
        </aside>
      )}

      {/* Overlay para mobile */}
      {isSidebarExpanded && isMobile && (
        <div
          className="fixed inset-0 bg-black/30 z-30 backdrop-blur-[2px]"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Header;
